import { createContext, useContext, useMemo, useState } from 'react'

import { useLocalStorage } from './useLocalStorage'
import * as authService from 'services/auth'
import { AuthResponse } from 'models/response'
import { trueErrorMessage } from 'utils/error'
import { jwtDecode } from 'jwt-decode'
import { trackEvent, AUTH_EVENTS } from 'services/mixpanel'

const AuthContext = createContext<any>(null)

export const AuthProvider = ({ children }: any) => {
	const [token, setToken] = useLocalStorage('token', null)
	const [refreshToken, setRefreshToken] = useLocalStorage('refreshToken', null)
	const [authInfo, setAuthInfo] = useLocalStorage('authInfo', null)
	const [registerUserName, setRegisterUserName] = useState<any>(null)
	const [requireOtp, setRequireOtp] = useState<any>(null)
	const [isModalLogin, setIsModalLogin] = useState<boolean>()
	const [isSignedUp, setIsSignedUp] = useLocalStorage('isSignedUp', false)
	const [impersonateMode, setImpersonateMode] = useLocalStorage('impersonateMode', false)

	const clearAuth = () => {
		// First, so a throwing storage call below can't leave a pending invite replayable for the next account
		authService.clearPendingInvite()
		authService.clearPendingPartnerConsent()
		// Clear React state
		setToken(null)
		setRefreshToken(null)
		setAuthInfo(null)
		setRequireOtp(null)
		setRegisterUserName(null)
		setIsModalLogin(false)
		setImpersonateMode(false)
		// Keep isSignedUp persistent

		// Clear localStorage
		const authKeys = ['token', 'refreshToken', 'authInfo']
		authKeys.forEach(x => localStorage.removeItem(x))
		// Keep isSignedUp in localStorage

		// Clear all draft messages (sendEventMessage_*) from localStorage
		Object.keys(localStorage).forEach(key => {
			if (key.startsWith('sendEventMessage_') || key.startsWith('sendInviteMessage_')) {
				localStorage.removeItem(key)
			}
		})
	}

	const register = (
		data: any,
		turnstileToken: string | null,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string, code?: string) => void
	) => {
		setRegisterUserName(data.userName)
		trackEvent(AUTH_EVENTS.SIGN_UP_STARTED, { method: 'email' })
		authService
			.passwordRegister(data, turnstileToken || undefined)
			.then((response: any) => {
				setToken(response.token)
				setRefreshToken(response.refreshToken)
				setAuthInfo({
					...response,
					agreeToTermsAndConditions: data.agreeToTermsAndConditions, //Temp agree while agree mutation was called
				})
				setIsSignedUp(true)
				trackEvent(AUTH_EVENTS.SIGN_UP_COMPLETED, { method: 'email', user_id: response.userId })
				onSuccess(response)
			})
			.catch((error: any) => {
				trackEvent(AUTH_EVENTS.SIGN_UP_FAILED, { method: 'email', error_message: trueErrorMessage(error.data) })
				onError(trueErrorMessage(error.data), error.data?.code)
			})
	}

	const resendRegisterEmail = (onSuccess: (data: AuthResponse) => void, onError: (reason: string) => void) => {
		authService
			.passwordResendRegisterEmail({ userName: registerUserName, refreshToken })
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const login = (
		data: any,
		turnstileToken: string | null,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void,
		isModal?: boolean
	) => {
		trackEvent(AUTH_EVENTS.SIGN_IN_ATTEMPTED, { method: 'email' })
		authService
			.passwordSignIn(data, turnstileToken || undefined)
			.then((response: any) => {
				setIsModalLogin(isModal)

				if (response.skipMultiFactorAuthentication) {
					setRequireOtp(false)
					setToken(response.token)
				} else {
					setRequireOtp(true)
				}
				setRefreshToken(response.refreshToken)
				setAuthInfo(response)

				trackEvent(AUTH_EVENTS.SIGN_IN_SUCCESS, {
					method: 'email',
					requires_otp: !response.skipMultiFactorAuthentication,
				})
				onSuccess(response)
			})
			.catch((error: any) => {
				trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, { method: 'email', error_message: trueErrorMessage(error.data) })
				onError(trueErrorMessage(error.data))
			})
	}

	const imLogin = (data: any, onSuccess: (data: AuthResponse) => void, onError: (reason: string) => void) => {
		authService
			.passwordImSignIn(data)
			.then((response: any) => {
				if (response.skipMultiFactorAuthentication) {
					setRequireOtp(false)
					setToken(response.token)

					// Decode token to check for impersonation
					if (response.token) {
						const decodedToken = jwtDecode<any>(response.token)
						if (decodedToken && decodedToken['impersonated-by-user-id']) {
							setImpersonateMode(true)
						} else {
							// Clear impersonation state if no impersonation
							setImpersonateMode(false)
						}
					}
				} else {
					setRequireOtp(true)
				}
				setRefreshToken(response.refreshToken)
				setAuthInfo(response)

				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	// onError gets the raw rejection, as phoneVerifyOtp does: a refused handle answers with a 400 the caller has to
	// tell apart from a transient failure, and the status and body are what say which.
	const phoneLogin = (
		data: any,
		onSuccess: (data: AuthResponse) => void,
		onError: (error: unknown) => void,
		isModal?: boolean
	) => {
		trackEvent(AUTH_EVENTS.SIGN_IN_ATTEMPTED, { method: 'phone' })
		authService
			.phoneStart(data)
			.then((response: any) => {
				setIsModalLogin(isModal)

				onSuccess(response)
			})
			.catch((error: any) => {
				trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, { method: 'phone', error_message: trueErrorMessage(error?.data) })
				onError(error)
			})
	}

	// onError gets the raw rejection, as emailCodeVerify does: a refused code carries a VerifyOtpResult body, while a
	// dead handle or a changed account carries an error code, and the caller has to tell them apart.
	const phoneVerifyOtp = (data: any, onSuccess: (data: AuthResponse) => void, onError: (error: unknown) => void) => {
		authService
			.phoneVerify(data)
			.then((response: any) => {
				setRequireOtp(false)
				setToken(response.token)
				setRefreshToken(response.refreshToken)
				setAuthInfo(response)

				trackEvent(AUTH_EVENTS.SIGN_IN_SUCCESS, { method: 'phone' })
				onSuccess(response)
			})
			.catch((error: any) => {
				trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, { method: 'phone', error_message: trueErrorMessage(error?.data) })
				onError(error)
			})
	}

	// onError gets the raw rejection: a refused code carries a VerifyOtpResult body the caller interprets
	const emailCodeVerify = (
		data: { emailAddress: string; code: string },
		onSuccess: (data: AuthResponse) => void,
		onError: (error: unknown) => void,
		isModal?: boolean
	) => {
		authService
			.emailVerify(data)
			.then((response: any) => {
				// A 200 without a session (e.g. the app's HTML from a misrouted proxy) is a failure, never a sign-in
				if (!response?.token) throw new Error('The sign-in response had no session')
				// The emailed code is the second factor: never route to MFA, whatever the account's MFA setting
				const session: AuthResponse = { ...response, skipMultiFactorAuthentication: true }
				setIsModalLogin(isModal)
				setRequireOtp(false)
				setToken(session.token)
				setRefreshToken(session.refreshToken)
				setAuthInfo(session)
				setIsSignedUp(true)

				trackEvent(AUTH_EVENTS.SIGN_IN_SUCCESS, { method: 'email_code', is_complete: session.isComplete })
				onSuccess(session)
			})
			.catch((error: any) => {
				trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, {
					method: 'email_code',
					error_message: trueErrorMessage(error?.data),
				})
				onError(error)
			})
	}

	const verifyOtp = (
		otp: string,
		purpose: string,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void
	) => {
		authService
			.verifyOtp({ refreshToken, otp, purpose })
			.then((response: any) => {
				setRequireOtp(false)
				setToken(response.token)
				setRefreshToken(response.refreshToken)
				setAuthInfo(response)

				trackEvent(AUTH_EVENTS.SIGN_IN_SUCCESS, { method: 'otp' })
				onSuccess(response)
			})
			.catch((error: any) => {
				trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, { method: 'otp', error_message: trueErrorMessage(error.data) })
				onError(trueErrorMessage(error.data))
			})
	}

	const resendOtp = (purpose: string, onSuccess: (data: AuthResponse) => void, onError: (reason: string) => void) => {
		authService
			.resendOtp({ refreshToken, purpose })
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const verifyEmailToken = (
		token: string,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void
	) => {
		authService
			.verifyEmailToken({ refreshToken: token })
			.then((response: any) => {
				setRequireOtp(false)
				setToken(response.token)
				setRefreshToken(response.refreshToken)
				setAuthInfo(response)

				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const validateToken = (
		token: string,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void
	) => {
		authService
			.validateToken({ token })
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const checkEmailAvailability = (
		data: any,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void
	) => {
		authService
			.checkEmailAvailability(data)
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const checkPhoneAvailability = (
		data: { phoneNumber: string; emailAddress?: string },
		onSuccess: (available: boolean) => void,
		onError: (reason: string) => void
	) => {
		authService
			.checkPhoneAvailability(data)
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const verifyEmailChangeToken = (
		token: string,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void
	) => {
		authService
			.confirmEmailChange({ token })
			.then((response: any) => {
				setToken(response.token)
				setRefreshToken(response.refreshToken)
				setAuthInfo(response)
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const logout = (
		onSuccess?: (data: AuthResponse) => void,
		onError?: (reason: string) => void,
		onFinal?: () => void
	) => {
		authService
			.logout()
			.then((response: any) => {
				trackEvent(AUTH_EVENTS.LOGOUT)
				onSuccess?.(response)
			})
			.catch((error: any) => {
				onError?.(trueErrorMessage(error.data))
			})
			.finally(() => {
				onFinal?.()
			})
	}

	const forgetPassword = (
		data: any,
		turnstileToken: string | null,
		onSuccess: (data: any) => void,
		onError: (reason: string) => void
	) => {
		authService
			.passwordForget(data, turnstileToken || undefined)
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const recoverPassword = (
		data: any,
		turnstileToken: string | null,
		onSuccess: (data: AuthResponse) => void,
		onError: (reason: string) => void
	) => {
		authService
			.passwordRecover(data, turnstileToken || undefined)
			.then((response: any) => {
				if (response.skipMultiFactorAuthentication) {
					setRequireOtp(false)
					setToken(response.token)
				} else {
					setRequireOtp(true)
				}
				setRefreshToken(response.refreshToken)

				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const startUpdatePassword = (onSuccess: (data: any) => void, onError: (reason: string) => void) => {
		authService
			.startUpdatePassword()
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	const updatePassword = (data: any, onSuccess: (data: any) => void, onError: (reason: string) => void) => {
		authService
			.updatePassword(data)
			.then((response: any) => {
				onSuccess(response)
			})
			.catch((error: any) => onError(trueErrorMessage(error.data)))
	}

	// Passkey Functions
	const registerPasskey = async (
		{ userName, displayName }: { userName: string; displayName: string },
		onSuccess: (data: any) => void,
		onError: (reason: string) => void
	) => {
		try {
			if (!authService.isPasskeySupported()) {
				onError('Passkeys are not supported on this device')
				return
			}

			trackEvent(AUTH_EVENTS.SIGN_UP_STARTED, { method: 'passkey' })
			const registerResult = await authService.registerPasskey({ userName, displayName })

			if (registerResult?.status !== 'ok') {
				onError('Passkey registration failed')
				return
			}

			// Registration succeeded — sign in with the new passkey to get the auth token
			setRegisterUserName(userName)
			setIsSignedUp(true)
			await loginPasskey(onSuccess, onError)
			trackEvent(AUTH_EVENTS.SIGN_UP_COMPLETED, { method: 'passkey' })
		} catch (error: any) {
			trackEvent(AUTH_EVENTS.SIGN_UP_FAILED, { method: 'passkey', error_message: error.message })
			onError(error.message)
		}
	}

	const loginPasskey = async (
		onSuccess: (data: any) => void,
		onError: (reason: string) => void,
		isModal?: boolean
	) => {
		try {
			if (!authService.isPasskeySupported()) {
				onError('Passkeys are not supported on this device')
				return
			}

			trackEvent(AUTH_EVENTS.SIGN_IN_ATTEMPTED, { method: 'passkey' })
			const response = await authService.loginPasskey()

			// Handle successful passkey login similar to password login
			setIsModalLogin(isModal)

			if (response.skipMultiFactorAuthentication) {
				setRequireOtp(false)
				setToken(response.token)
			} else {
				setRequireOtp(true)
			}
			setRefreshToken(response.refreshToken)
			setAuthInfo(response)

			trackEvent(AUTH_EVENTS.SIGN_IN_SUCCESS, { method: 'passkey' })
			onSuccess(response)
		} catch (error: any) {
			trackEvent(AUTH_EVENTS.SIGN_IN_FAILED, { method: 'passkey', error_message: error.message })
			onError(error.message)
		}
	}

	const isPasskeySupported = () => {
		return authService.isPasskeySupported()
	}

	const value = useMemo(
		() => ({
			requireOtp,
			isModalLogin,
			token,
			refreshToken,
			authInfo,
			impersonateMode,
			setToken,
			setRefreshToken,
			setAuthInfo,
			setImpersonateMode,
			setIsSignedUp,
			isSignedUp,
			register,
			resendRegisterEmail,
			login,
			verifyOtp,
			resendOtp,
			verifyEmailToken,
			logout,
			forgetPassword,
			recoverPassword,
			clearAuth,
			validateToken,
			checkEmailAvailability,
			checkPhoneAvailability,
			verifyEmailChangeToken,
			startUpdatePassword,
			updatePassword,
			imLogin,
			phoneLogin,
			phoneVerifyOtp,
			emailCodeVerify,
			registerPasskey,
			loginPasskey,
			isPasskeySupported,
		}),
		[requireOtp, isModalLogin, token, refreshToken, JSON.stringify(authInfo), impersonateMode]
	)
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
	const context = useContext(AuthContext)
	if (context === null) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}
